/**
 * Monthlees first-touch ad attribution.
 * Captures click IDs + UTMs on landing, persists 30 days, syncs once into cart
 * attributes as `_attr_*` for order note_attributes (backend parser).
 *
 * Must load early (defer in <head>) so the fetch wrapper is active before ATC.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'monthlees_attr_v1';
  var SYNC_FLAG_PREFIX = 'monthlees_attr_synced_';
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var LANDING_MAX = 500;
  var ATTR_PREFIX = '_attr_';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var CLICK_KEYS = ['fbclid', 'gclid', 'ttclid', 'axon_id'];
  // Referral code — never `ref` (GoAffPro). Survives as cart `_attr_r`.
  var REFERRAL_KEY = 'r';
  // Strip before persisting landing_page — never carry guest/session identifiers for 30 days.
  var LANDING_STRIP_PARAMS = ['email', 'e', 'token', 'pss_anon_id', 'customer_id'];
  var AUDIENCE_PREFIXES = [
    { re: /^legacy([_-]|$)/i, type: 'legacy_list' },
    { re: /^lookalike([_-]|$)/i, type: 'lookalike' },
    { re: /^prospect(ing)?([_-]|$)/i, type: 'prospecting' },
    { re: /^retarget(ing)?([_-]|$)/i, type: 'retargeting' },
    { re: /^winback([_-]|$)/i, type: 'winback' },
  ];

  var syncInFlight = false;
  var originalFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  function nowIso() {
    try {
      return new Date().toISOString();
    } catch (e) {
      return '';
    }
  }

  function safeGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  function parseStored(raw) {
    if (!raw) return null;
    try {
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      if (data.first_touch_at) {
        var ts = Date.parse(data.first_touch_at);
        if (!isNaN(ts) && Date.now() - ts > TTL_MS) {
          try {
            window.localStorage.removeItem(STORAGE_KEY);
          } catch (e) {
            /* ignore */
          }
          return null;
        }
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function hasPaidSignal(rec) {
    if (!rec) return false;
    var i;
    for (i = 0; i < CLICK_KEYS.length; i += 1) {
      if (rec[CLICK_KEYS[i]]) return true;
    }
    if (rec.utm_source && rec.utm_medium) return true;
    return false;
  }

  function readParams() {
    var out = {};
    try {
      var params = new URLSearchParams(window.location.search || '');
      var i;
      for (i = 0; i < UTM_KEYS.length; i += 1) {
        var u = params.get(UTM_KEYS[i]);
        if (u) out[UTM_KEYS[i]] = String(u).slice(0, 200);
      }
      for (i = 0; i < CLICK_KEYS.length; i += 1) {
        var c = params.get(CLICK_KEYS[i]);
        if (c) out[CLICK_KEYS[i]] = String(c).slice(0, 200);
      }
      // Axon accepts axon_id or axid
      if (!out.axon_id) {
        var axid = params.get('axid');
        if (axid) out.axon_id = String(axid).slice(0, 200);
      }
      // Monthlees referral — ?r= only (not ?ref=)
      var rCode = params.get(REFERRAL_KEY);
      if (rCode) out[REFERRAL_KEY] = String(rCode).slice(0, 64);
    } catch (e) {
      /* ignore */
    }
    return out;
  }

  function stripSensitiveSearch(search) {
    try {
      var raw = String(search || '');
      if (!raw || raw === '?') return '';
      var params = new URLSearchParams(raw.charAt(0) === '?' ? raw.slice(1) : raw);
      var i;
      for (i = 0; i < LANDING_STRIP_PARAMS.length; i += 1) {
        params.delete(LANDING_STRIP_PARAMS[i]);
      }
      var kept = params.toString();
      return kept ? '?' + kept : '';
    } catch (e) {
      return String(search || '').slice(0, LANDING_MAX);
    }
  }

  function buildLanding() {
    try {
      var landing = String(window.location.pathname || '') + stripSensitiveSearch(window.location.search || '');
      if (landing.length > LANDING_MAX) landing = landing.slice(0, LANDING_MAX);
      return landing;
    } catch (e) {
      return '';
    }
  }

  function deriveAudienceType(utmCampaign) {
    if (!utmCampaign) return null;
    var campaign = String(utmCampaign);
    var i;
    for (i = 0; i < AUDIENCE_PREFIXES.length; i += 1) {
      if (AUDIENCE_PREFIXES[i].re.test(campaign)) return AUDIENCE_PREFIXES[i].type;
    }
    return null;
  }

  function captureFromPage() {
    var incoming = readParams();
    var existing = parseStored(safeGet(STORAGE_KEY));
    var paidIncoming = hasPaidSignal(incoming);
    var paidExisting = hasPaidSignal(existing);

    // First touch wins, unless paid claim on organic-only record.
    // Still merge a late ?r= onto an existing record when r is empty — referral
    // codes must not be dropped because an organic/paid first touch already exists.
    if (existing && !(paidIncoming && !paidExisting)) {
      if (incoming[REFERRAL_KEY] && !existing[REFERRAL_KEY]) {
        existing[REFERRAL_KEY] = incoming[REFERRAL_KEY];
        safeSet(STORAGE_KEY, JSON.stringify(existing));
      }
      return existing;
    }

    var next = {
      first_touch_at: (existing && existing.first_touch_at) || nowIso(),
      landing_page: buildLanding(),
      referrer: '',
    };
    try {
      next.referrer = String(document.referrer || '').slice(0, 500);
    } catch (e) {
      next.referrer = '';
    }

    var key;
    for (key in incoming) {
      if (Object.prototype.hasOwnProperty.call(incoming, key) && incoming[key]) {
        next[key] = incoming[key];
      }
    }
    // Preserve prior r across a paid claim if the new URL has none
    if (!next[REFERRAL_KEY] && existing && existing[REFERRAL_KEY]) {
      next[REFERRAL_KEY] = existing[REFERRAL_KEY];
    }

    // Paid claim keeps organic first_touch_at? Spec: paid should claim — refresh stamp.
    if (paidIncoming && existing && !paidExisting) {
      next.first_touch_at = nowIso();
      next.landing_page = buildLanding();
    }

    safeSet(STORAGE_KEY, JSON.stringify(next));
    return next;
  }

  function getRecord() {
    return parseStored(safeGet(STORAGE_KEY)) || captureFromPage();
  }

  function toCartAttributes(rec) {
    var attrs = {};
    if (!rec) return attrs;
    var keys = UTM_KEYS.concat(CLICK_KEYS).concat([REFERRAL_KEY, 'landing_page', 'referrer', 'first_touch_at']);
    var i;
    for (i = 0; i < keys.length; i += 1) {
      var k = keys[i];
      var v = rec[k];
      if (v == null || v === '') continue;
      attrs[ATTR_PREFIX + k] = String(v);
    }
    return attrs;
  }

  function properties() {
    var rec = getRecord();
    var out = {};
    if (!rec) return out;
    var key;
    for (key in rec) {
      if (Object.prototype.hasOwnProperty.call(rec, key) && rec[key] != null && rec[key] !== '') {
        out[key] = rec[key];
      }
    }
    out.is_paid = hasPaidSignal(rec);
    return out;
  }

  /** Lean props for PostHog — no landing_page / referrer URL bloat. */
  function campaignProperties() {
    var rec = getRecord() || {};
    var out = {};
    var i;
    for (i = 0; i < UTM_KEYS.length; i += 1) {
      if (rec[UTM_KEYS[i]]) out[UTM_KEYS[i]] = rec[UTM_KEYS[i]];
    }
    for (i = 0; i < CLICK_KEYS.length; i += 1) {
      if (rec[CLICK_KEYS[i]]) out[CLICK_KEYS[i]] = rec[CLICK_KEYS[i]];
    }
    if (rec[REFERRAL_KEY]) out[REFERRAL_KEY] = rec[REFERRAL_KEY];
    out.is_paid = hasPaidSignal(rec);
    var audience = deriveAudienceType(rec.utm_campaign);
    if (audience) out.audience_type = audience;
    return out;
  }

  function cartTokenFrom(cart) {
    if (!cart) return '';
    return String(cart.token || cart.id || '');
  }

  function alreadySynced(token) {
    if (!token) return false;
    return sessionGet(SYNC_FLAG_PREFIX + token) === '1';
  }

  function markSynced(token) {
    if (!token) return;
    sessionSet(SYNC_FLAG_PREFIX + token, '1');
  }

  function syncToCart(force) {
    if (syncInFlight) return Promise.resolve(null);
    var attrs = toCartAttributes(getRecord());
    var keys = Object.keys(attrs);
    if (!keys.length) return Promise.resolve(null);

    syncInFlight = true;
    var fetchFn = originalFetch || window.fetch;
    if (!fetchFn) {
      syncInFlight = false;
      return Promise.resolve(null);
    }

    return fetchFn
      .call(window, '/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      .then(function (res) {
        if (!res || !res.ok) return null;
        return res.json();
      })
      .then(function (cart) {
        if (!cart) return null;
        var token = cartTokenFrom(cart);
        if (!force && alreadySynced(token)) return cart;

        // Skip if cart already has our attrs
        var existing = cart.attributes || {};
        var needsWrite = force;
        var i;
        if (!needsWrite) {
          for (i = 0; i < keys.length; i += 1) {
            if (String(existing[keys[i]] || '') !== String(attrs[keys[i]])) {
              needsWrite = true;
              break;
            }
          }
        }
        if (!needsWrite) {
          markSynced(token);
          return cart;
        }

        return fetchFn
          .call(window, '/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ attributes: attrs }),
          })
          .then(function (res) {
            if (res && res.ok) markSynced(token);
            return res && res.ok ? res.json() : null;
          });
      })
      .catch(function () {
        return null;
      })
      .then(function (result) {
        syncInFlight = false;
        return result;
      });
  }

  function urlLooksLikeCartAdd(url) {
    try {
      var u = typeof url === 'string' ? url : url && url.url ? String(url.url) : '';
      if (!u) return false;
      // Absolute or relative
      if (u.indexOf('/cart/add') !== -1) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  function installFetchWrapper() {
    if (!originalFetch || window.__monthleesAttrFetchWrapped) return;
    window.__monthleesAttrFetchWrapped = true;

    window.fetch = function (input, init) {
      var req = originalFetch(input, init);
      try {
        if (urlLooksLikeCartAdd(input)) {
          req.then(function (response) {
            try {
              if (response && response.ok) {
                // Clone not needed — we only trigger sync, don't consume body.
                syncToCart(false);
              }
            } catch (e) {
              /* never break ATC */
            }
            return response;
          }).catch(function () {
            /* ignore */
          });
        }
      } catch (e) {
        /* ignore */
      }
      return req;
    };
  }

  function injectAttrInputs(form) {
    try {
      if (!form || form.getAttribute('data-ml-attr-injected') === '1') return;
      var attrs = toCartAttributes(getRecord());
      var keys = Object.keys(attrs);
      if (!keys.length) {
        form.setAttribute('data-ml-attr-injected', '1');
        return;
      }
      var i;
      for (i = 0; i < keys.length; i += 1) {
        var name = 'attributes[' + keys[i] + ']';
        var existing = form.querySelector('input[name="' + name.replace(/"/g, '\\"') + '"]');
        if (existing) {
          existing.value = attrs[keys[i]];
          continue;
        }
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = attrs[keys[i]];
        input.setAttribute('data-ml-attr-field', '1');
        form.appendChild(input);
      }
      form.setAttribute('data-ml-attr-injected', '1');
    } catch (e) {
      /* never break forms */
    }
  }

  function refreshAttrInputs(form) {
    try {
      if (!form) return;
      form.removeAttribute('data-ml-attr-injected');
      var stale = form.querySelectorAll('input[data-ml-attr-field="1"]');
      var i;
      for (i = 0; i < stale.length; i += 1) {
        if (stale[i].parentNode) stale[i].parentNode.removeChild(stale[i]);
      }
      injectAttrInputs(form);
    } catch (e) {
      /* ignore */
    }
  }

  function beaconCartAttributes() {
    try {
      var attrs = toCartAttributes(getRecord());
      if (!Object.keys(attrs).length) return;
      var body = JSON.stringify({ attributes: attrs });
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        var blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon('/cart/update.js', blob);
        return;
      }
      // Fallback if sendBeacon unavailable — fire-and-forget fetch
      var fetchFn = originalFetch || window.fetch;
      if (fetchFn) {
        fetchFn.call(window, '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: body,
          keepalive: true,
        }).catch(function () {});
      }
    } catch (e) {
      /* ignore */
    }
  }

  function installFormAttribution() {
    function scanForms(root) {
      try {
        var scope = root && root.querySelectorAll ? root : document;
        var forms = scope.querySelectorAll
          ? scope.querySelectorAll('form[action*="/cart/add"]')
          : [];
        // If root itself is a matching form
        if (root && root.matches && root.matches('form[action*="/cart/add"]')) {
          refreshAttrInputs(root);
        }
        var i;
        for (i = 0; i < forms.length; i += 1) {
          refreshAttrInputs(forms[i]);
        }
      } catch (e) {
        /* ignore */
      }
    }

    scanForms(document);

    // Refresh inputs immediately before submit (record may have updated).
    // Beacon fallback is installed only if verification shows hidden inputs fail.
    document.addEventListener(
      'submit',
      function (event) {
        try {
          var form = event.target;
          if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
          refreshAttrInputs(form);
          if (window.__monthleesAttrUseBeaconFallback) {
            beaconCartAttributes();
          }
        } catch (e) {
          /* form must still submit */
        }
      },
      true
    );

    try {
      var obs = new MutationObserver(function (mutations) {
        var m;
        for (m = 0; m < mutations.length; m += 1) {
          var nodes = mutations[m].addedNodes;
          var n;
          for (n = 0; n < nodes.length; n += 1) {
            var node = nodes[n];
            if (!node || node.nodeType !== 1) continue;
            if (node.matches && node.matches('form[action*="/cart/add"]')) {
              refreshAttrInputs(node);
            } else if (node.querySelectorAll) {
              scanForms(node);
            }
          }
        }
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {
      /* ignore */
    }
  }

  function bindCartUiResync() {
    document.addEventListener(
      'click',
      function (event) {
        try {
          var t = event.target;
          if (!t || !t.closest) return;
          if (
            t.closest('[data-monthlees-cart-trigger]') ||
            t.closest('[data-cart-drawer-open]') ||
            t.closest('a[href="/cart"]') ||
            t.closest('a[href$="/cart"]')
          ) {
            setTimeout(function () {
              syncToCart(true);
            }, 400);
          }
        } catch (e) {
          /* ignore */
        }
      },
      true
    );

    // Cart page load
    try {
      if ((window.location.pathname || '').indexOf('/cart') === 0) {
        setTimeout(function () {
          syncToCart(true);
        }, 300);
      }
    } catch (e) {
      /* ignore */
    }

    // Drawer open class observer
    try {
      var root = document.documentElement;
      var obs = new MutationObserver(function () {
        if (root.classList.contains('monthlees-cart-drawer-open')) {
          syncToCart(true);
        }
      });
      obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    } catch (e) {
      /* ignore */
    }
  }

  // Capture immediately
  captureFromPage();
  installFetchWrapper();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindCartUiResync();
      installFormAttribution();
    });
  } else {
    bindCartUiResync();
    installFormAttribution();
  }

  window.MonthleesAttribution = {
    get: function () {
      return getRecord();
    },
    isPaid: function () {
      return hasPaidSignal(getRecord());
    },
    properties: properties,
    campaignProperties: campaignProperties,
    syncToCart: syncToCart,
    /** Enable sendBeacon fallback on form submit (set after verification if needed). */
    enableBeaconFallback: function () {
      window.__monthleesAttrUseBeaconFallback = true;
    },
  };
})();
